import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import SubmitButton from '../../components/submit-button/submit-button';

// Form Data
import { formDataGalleryCreate } from '../../form-data/form-data-gallery-create';

// Helpers
import {
  getGalleryData,
  handleFormOnChange,
  deleteAttachment,
  maxAttachments,
  uploadDroppedFiles,
  useRevokePreviewUrlsOnUnmount,
  handleStudentChange,
  checkIfFirstStepWasFilled,
  checkIfAllStepsWereFilled,
  buildGalleryRequest,
  filesToImagePreviewList,
} from '../../helpers/gallery-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ClassroomService from '../../services/classroom-service';
import GalleryService from '../../services/gallery-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';
import FileStorageService from '../../services/file-storage-service';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';
import ImagePreview from '../../components/image-preview/image-preview';
import StudentSelector from '../../components/student-selector/student-selector';

const GalleryCreate = () => {
  const navigate = useNavigate();
  const { setToastContent, setModalContent } = useContext(GeneralContext);
  const [error, setError] = useState(false);

  const initialData: GalleryFormData = getGalleryData();
  const [data, setData] = useState<GalleryFormData>(initialData);
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const [validations, setValidations] = useState({});
  const [allowNextPage, setAllowNext] = useState(false);
  const [allowSubmit, setAllowSubmit] = useState(false);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useRevokePreviewUrlsOnUnmount(data);

  useEffect(() => {
    checkIfFirstStepWasFilled(data, validations, allowNextPage, setAllowNext);
    checkIfAllStepsWereFilled(data, validations, allowSubmit, setAllowSubmit);
  }, [validations, allowNextPage, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  const [galleryService, setGalleryService] = useState<GalleryService | null>(null);
  const [fileStorageService, setFileStorageService] = useState<FileStorageService | null>(null);
  useEffect(() => {
    const classroomService = new ClassroomService();
    const schoolService = new SchoolService();
    const storageService = new StorageService();
    const schoolId = storageService.getEmployee().schoolId;

    Promise.all([
      classroomService.listClassroomStudents(),
      schoolService.getSchoolById(schoolId),
    ])
      .then(([classrooms, school]) => {
        setClassroomList(classrooms);
        setGalleryService(new GalleryService(school.uuid));
        setFileStorageService(new FileStorageService(school.uuid));
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileDrop = (item) => {
    if (!item) return;

    uploadDroppedFiles({
      files: item.files,
      data,
      setData,
      fileStorageService,
      setIsLoadingAttachment,
      setUploadProgress,
      setModalContent,
      setToastContent,
    });
  };

  const handleSubmit = () => {
    // Um duplo clique criaria duas galerias, cada uma com o seu push.
    if (isSubmitting) return;
    setIsSubmitting(true);

    galleryService
      .createGallery(buildGalleryRequest(data, classroomList))
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Galeria enviada!',
          type: 'success',
        });
        navigate('/galerias/listar');
      })
      .catch(() => {
        setIsSubmitting(false);
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao criar a galeria.',
          type: 'error',
        });
      });
  };

  const handleNextStep = () => {
    setStep(1)
  }

  const handlePreviousStep = () => {
    setStep(0)
  }

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomList || !galleryService || !fileStorageService)
    return <Loading type='contentFull' />;

  if (step === 0)
    return (<BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={formDataGalleryCreate(data)}
        validations={validations}
        handleOnChange={(evt) => handleFormOnChange(evt, validations, setValidations, setData)}
      />
      {classroomList && (
        <ImagePreview
          maxFiles={maxAttachments}
          imageList={filesToImagePreviewList(data.formData.attachments)}
          onDrop={handleFileDrop}
          onDelete={(fileId: string) => deleteAttachment(fileId, setData)}
          uploadProgress={uploadProgress}
          isUploading={isLoadingAttachment}
        />
      )}
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/galerias/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='próximo'
          variant='primary'
          onClick={handleNextStep}
          disabled={!allowNextPage}
        />
      </ButtonsBar>
    </BaseContentContainer>);
  else
    return (<BaseContentContainer
      format={BaseContentContainerFormat.singleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      {classroomList && (
        <StudentSelector
          classrooms={classroomList}
          handleStudentChange={(
            selectedClassrooms: number[],
            selectedStudents: number[]
          ) =>
            handleStudentChange(
              selectedClassrooms,
              selectedStudents,
              data,
              setData
            )
          }
        />
      )}

      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <SubmitButton value='voltar' variant='terciary' onClick={handlePreviousStep} />
        <SubmitButton
          value='publicar galeria'
          variant='primary'
          onClick={handleSubmit}
          disabled={!allowSubmit || isSubmitting}
        />
      </ButtonsBar>
    </BaseContentContainer>);
};

export default GalleryCreate;
